/**
 * `resetOrgScopedStores` — clear every client-side store that holds
 * data scoped to the currently-active organisation (W102-F3-S1).
 *
 * Background
 * ----------
 * When a user switches between orgs via the topbar, the rule is "the
 * new org's session must look like a fresh login." React-Query handles
 * its own slice via `queryClient.clear()` in `useSwitchOrg`. Anything
 * outside React-Query (Zustand stores, plain module-level singletons,
 * IndexedDB caches we author ourselves) needs its own purge — otherwise
 * data from org A would briefly bleed through into org B's session
 * until the relevant component re-derives.
 *
 * Pattern
 * -------
 * Stores register a resetter at module load via `registerOrgScopedResetter`.
 * `useSwitchOrg` calls `resetOrgScopedStores()` after `setActivePartner`,
 * which iterates the registry and invokes each. Idempotent — calling
 * twice is a no-op the second time as long as resetters are safe to
 * call against an already-empty store (the convention).
 *
 * Audit of stores at the time of W102-F3-S1
 * -----------------------------------------
 * - `components/feedback/feedback-store.ts` — Zustand. Holds the global
 *   feedback-modal open/closed state + the most-recent screenshot blob.
 *   **NOT org-scoped** — it's user-session-scoped (the feedback panel
 *   should keep its open state across an org switch, that's not user-
 *   facing data). Does NOT register a resetter.
 *
 * No other Zustand stores exist in apps/web today. If the future adds
 * `useBuildingsCacheStore`, `usePortfolioFiltersStore`, etc. that hold
 * org-scoped data (e.g. "last viewed building id in org X"), those
 * stores MUST call `registerOrgScopedResetter(() => store.reset())` at
 * module-eval time so a switch purges them.
 *
 * Lives outside React so non-component code (api-fetch retry queues,
 * polling intervals) can also subscribe to the lifecycle if needed.
 */

type Resetter = () => void;

const resetters = new Set<Resetter>();

/**
 * Register a function to be called on every org switch. Returns an
 * unregister callback for tests + HMR cleanup.
 *
 * The resetter MUST be safe to call against an already-empty store —
 * we may invoke it multiple times in tests, in StrictMode double-mount
 * sequences, or in retry flows.
 */
export function registerOrgScopedResetter(fn: Resetter): () => void {
  resetters.add(fn);
  return () => {
    resetters.delete(fn);
  };
}

/**
 * Call every registered resetter. Catches per-resetter throws so one
 * broken store can't block the rest of the switch.
 */
export function resetOrgScopedStores(): void {
  for (const fn of resetters) {
    try {
      fn();
    } catch (err) {
      // Best-effort: log to console so a broken resetter is visible
      // without taking down the org switch. Production observability
      // catches this via the same console-error → Sentry bridge that
      // the rest of the app uses.
       
      console.error('[resetOrgScopedStores] resetter threw:', err);
    }
  }
}

/**
 * Test helper — drops every registered resetter. Use in vitest
 * `beforeEach` so per-test registrations don't leak into the next test.
 */
export function __clearOrgScopedResettersForTests(): void {
  resetters.clear();
}
